import { odataGet, odataPost, odataPatch, odataDelete, odataEscape } from './dataverse';
import type { ComposeRole } from './identity';

/**
 * Compose user-access management. Implements ACCESS-ROLES-PATTERN.md
 * Piece 2 (the Microsoft user picker) and Piece 4 (role-table CRUD against
 * gaqc_composerole).
 */

export type PickerUser = { name: string; email: string };

/**
 * Populate the "add a person" dropdown from real interactive Dataverse
 * users. The filter strips disabled, non-interactive, and
 * application/service accounts (verbatim from the pattern doc).
 *
 * CRITICAL CONTRACT: store `domainname` (the UPN) LOWERCASED as gaqc_name.
 * The sign-in role lookup keys on MSAL account.username lowercased, which
 * equals systemuser.domainname.
 */
export async function fetchPickerUsers(): Promise<PickerUser[]> {
  const flt = 'isdisabled eq false and accessmode eq 0 and applicationid eq null';
  const r = await odataGet<{ fullname?: string; domainname?: string; internalemailaddress?: string }>(
    'systemusers',
    `?$select=fullname,domainname,internalemailaddress&$filter=${encodeURIComponent(flt)}`
  );
  return (r.value ?? [])
    .map((u) => ({
      name: u.fullname || '',
      email: String(u.domainname || u.internalemailaddress || '').toLowerCase(),
    }))
    .filter((u) => u.email)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export type RoleAssignment = {
  gaqc_composeroleid?: string;
  gaqc_name: string; // UPN lowercased
  gaqc_role: ComposeRole;
  gaqc_displayname?: string;
};

export async function fetchRoleAssignments(): Promise<RoleAssignment[]> {
  const r = await odataGet<RoleAssignment>(
    'gaqc_composeroles',
    '?$select=gaqc_composeroleid,gaqc_name,gaqc_role,gaqc_displayname&$orderby=gaqc_name asc&$top=500'
  );
  return r.value ?? [];
}

/** Probe-then-PATCH/POST upsert by UPN (don't rely on alt-key timing). */
export async function upsertRoleAssignment(
  upn: string,
  role: ComposeRole,
  displayName: string
): Promise<void> {
  const key = upn.toLowerCase();
  const found = await odataGet<{ gaqc_composeroleid: string }>(
    'gaqc_composeroles',
    `?$select=gaqc_composeroleid&$filter=gaqc_name eq '${odataEscape(key)}'&$top=1`
  );
  if (found.value?.length) {
    await odataPatch('gaqc_composeroles', found.value[0].gaqc_composeroleid, {
      gaqc_role: role,
      gaqc_displayname: displayName,
    });
  } else {
    await odataPost('gaqc_composeroles', {
      gaqc_name: key,
      gaqc_role: role,
      gaqc_displayname: displayName,
    });
  }
}

export async function removeRoleAssignment(id: string): Promise<void> {
  await odataDelete('gaqc_composeroles', id);
}
